export interface SupabaseBrowserEnv {
  readonly DEV?: boolean;
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
}

export interface SupabaseBrowserConfig {
  readonly url: string;
  readonly anonKey: string;
}

export const SUPABASE_REQUIRED_ENV_KEYS = [
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_ANON_KEY",
] as const;

export function isSupabaseConfigured(
  env: SupabaseBrowserEnv = import.meta.env,
): boolean {
  return (
    isNonEmptyValue(env.VITE_SUPABASE_URL) &&
    isNonEmptyValue(env.VITE_SUPABASE_ANON_KEY)
  );
}

export function resolveSupabaseBrowserConfig(
  env: SupabaseBrowserEnv = import.meta.env,
): SupabaseBrowserConfig {
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;
  const missingKeys = SUPABASE_REQUIRED_ENV_KEYS.filter((key) => {
    const value = env[key];
    return !isNonEmptyValue(value);
  });

  if (missingKeys.length > 0) {
    throw new Error(buildSupabaseConfigErrorMessage(env, missingKeys));
  }

  if (!isNonEmptyValue(url) || !isNonEmptyValue(anonKey)) {
    throw new Error(buildSupabaseConfigErrorMessage(env, missingKeys));
  }

  return {
    url: url.trim(),
    anonKey: anonKey.trim(),
  };
}

function buildSupabaseConfigErrorMessage(
  env: SupabaseBrowserEnv,
  missingKeys: readonly string[],
): string {
  const prefix = env.DEV
    ? "Development error: Supabase browser client is not configured."
    : "Supabase browser client is not configured.";

  return [
    prefix,
    `Missing environment variables: ${missingKeys.join(", ")}.`,
    "Set them in a local .env file before using Supabase-backed features.",
  ].join(" ");
}

function isNonEmptyValue(value: string | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
